<script setup lang="ts">
type LocaleCode = "ar" | "en";

const { t, locale } = useI18n();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();
const site = useSiteData();
const { localized } = useLocaleValue();
const route = useRoute();

const isActive = (targetPath: string) => {
  const current = route.path;
  const target = localePath(targetPath);
  return current === target;
};

const currentLocale = computed(() => locale.value as LocaleCode);
const availableLocales: { key: LocaleCode; label: string }[] = [
  { key: "ar", label: "العربية" },
  { key: "en", label: "English" },
];

const dropdownOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const toggleDropdown = (event: MouseEvent) => {
  event.stopPropagation();
  dropdownOpen.value = !dropdownOpen.value;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

if (import.meta.client) {
  onMounted(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (dropdownRef.value && target && !dropdownRef.value.contains(target)) {
        closeDropdown();
      }
    };

    document.addEventListener("click", handleClickOutside);

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });
  });
}
</script>

<template>
  <nav class="navbar">
    <div class="container">
      <div class="nav-brand">
        <NuxtLink :to="localePath('/')">
          <h1>{{ localized(site.website_name) }}</h1>
        </NuxtLink>
      </div>
      <ul class="nav-menu">
        <li>
          <NuxtLink
            :to="localePath('/')"
            :class="{ active: isActive('/') }"
          >
            {{ t("header.nav.home") }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            :to="localePath('/about/')"
            :class="{ active: isActive('/about/') }"
          >
            {{ t("header.nav.about") }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink :to="localePath('/#services')">
            {{ t("header.nav.services") }}
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            :to="localePath('/contact/')"
            :class="{ active: isActive('/contact/') }"
          >
            {{ t("header.nav.contact") }}
          </NuxtLink>
        </li>
        <li class="nav-language">
          <div class="language-dropdown" ref="dropdownRef">
            <button
              type="button"
              class="language-toggle"
              :aria-label="t('header.language_select')"
              :aria-expanded="dropdownOpen"
              @click="toggleDropdown"
            >
              <i class="fas fa-globe" aria-hidden="true"></i>
            </button>
            <div v-if="dropdownOpen" class="language-menu">
              <NuxtLink
                v-for="l in availableLocales"
                :key="l.key"
                :to="switchLocalePath(l.key)"
                :class="{ active: l.key === currentLocale }"
                @click="closeDropdown"
              >
                {{ l.label }}
              </NuxtLink>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </nav>
</template>

